import React from "react";
import { formatNumber } from "@danishagro/shared/src/helpers/formatNumber.helper";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import { Helmet } from "react-helmet";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import { DA_Button } from "@danishagro/shared/src/components/atoms/Button/Button.component";
import { useDynamicOverlay } from "@danishagro/shared/src/hooks/dynamicOverlay/useDynamicOverlay.hook";
import S from "./DiscountTableMobile.module.scss";

interface DiscountRow {
    quantityFrom: number;
    quantityTo?: number;
    price: number;
}

interface DiscountTableProps {
    discountTable: DiscountRow[];
    priceCurrency?: string;
    currentQuantity?: number;
    onRowClick?: (quantity: number) => void;
    clickableRows?: boolean;
}

export const DiscountTableMobile = ({
    discountTable,
    priceCurrency,
    onRowClick,
    clickableRows,
    currentQuantity,
}: DiscountTableProps) => {
    const { getDictionaryString } = useTranslations();
    const { currentCulture } = useAppData();
    const { closeOverlay } = useDynamicOverlay();

    const [markedRowQuantity, setMarkedRowQuantity] = React.useState<number | null>(() => {
        if (typeof currentQuantity === "number") {
            const matchingRow = discountTable.find(
                (item) =>
                    currentQuantity >= item.quantityFrom &&
                    currentQuantity <= (item.quantityTo || Infinity)
            );
            return matchingRow?.quantityFrom ?? null;
        }
        return null;
    });

    // Find the active row (the row that matches `currentQuantity`)
    const activeRowIndex = discountTable.findIndex(
        (item) =>
            markedRowQuantity !== null &&
            markedRowQuantity >= item.quantityFrom &&
            markedRowQuantity <= (item.quantityTo || Infinity)
    );

    const structuredData = {
        "@context": "https://schema.org",
        "@type": "AggregateOffer",
        offers: discountTable.map((item) => ({
            "@type": "Offer",
            price: formatNumber(item.price, currentCulture, { decimals: 2 }),
            priceCurrency: priceCurrency,
            eligibleQuantity: {
                "@type": "QuantitativeValue",
                value: item.quantityFrom,
            },
        })),
    };

    return (
        <div className={S.discountTable}>
            <Helmet>
                <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
            </Helmet>
            <table className={S.discountTable}>
                <tbody>
                    {discountTable.map((item, index) => (
                        <tr
                            key={index}
                            className={cn(index === activeRowIndex && S.activeRow)}
                            onClick={() => {
                                if (clickableRows && onRowClick) {
                                    setMarkedRowQuantity(item.quantityFrom);
                                }
                            }}
                        >
                            <td className={S.firstColumn}>
                                <span>{item.quantityFrom}</span>
                                <span>{getDictionaryString("pcs")}</span>
                                <span className={S.pricePerPcs}>
                                    {getDictionaryString("discount.PricePerPcs")}
                                </span>
                            </td>
                            <td className={cn(S.alignRight, S.secondColumn)}>
                                {formatNumber(item.price, currentCulture, { decimals: 2 })}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <div className={S.drawerButtonsWrapper}>
                <DA_Button
                    onClick={closeOverlay}
                    isGhost={true}
                    title={getDictionaryString("close")}
                />
                <DA_Button
                    onClick={() => {
                        onRowClick(markedRowQuantity);
                        closeOverlay();
                    }}
                    disabled={!markedRowQuantity}
                    title={
                        !markedRowQuantity
                            ? `${getDictionaryString("amount")}`
                            : ` ${getDictionaryString(
                                  "amount"
                              )}: ${markedRowQuantity} ${getDictionaryString("pcs")}`
                    }
                />
            </div>
        </div>
    );
};
